import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Sparkles, Eye, Wind, Compass } from "lucide-react";
import { http, gradeLabel, edition } from "@/lib/api";
import { STATE_NAMES } from "@/lib/brand";
import { PricingSection } from "@/pages/Pricing";
import { SubjectIcon } from "@/lib/subjects";
import { useProfile } from "@/context/ProfileContext";
import { StatsStrip } from "@/components/StatsStrip";
import { SchoolsCTA } from "@/components/SchoolsCTA";
import { AssignedSection } from "@/components/AssignedSection";
import { TodayGoal } from "@/components/buddy/TodayGoal";
import { StudentDashboard } from "@/components/dashboard/StudentDashboard";
import { CheckInPrompt } from "@/components/assessment/CheckInPrompt";
import { BadgeShelf } from "@/components/BadgeShelf";
import { MyGoal, WordsLink } from "@/components/words/HomeCards";
import { pathwayEligible } from "@/components/Header";
import { Briefcase } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/0b8529c7-db2f-4cc8-ac82-6f8c699f5710/images/1e802b3ba10607067589f308c45aeccfa3c9f20e307fce18eebe16642f98d035.jpeg";
const QUICK_SUBJECTS = ["science", "math", "ela"];

const PILLARS = [
  { icon: Eye, title: "Know who you really are", text: "Every subject is a mirror. Math reveals the order-seeking mind, science the living connection to nature, history the roots and responsibility we carry." },
  { icon: Wind, title: "Inner active learning", text: "Students don't just read about ideas; they turn attention inward, sensing, imagining, noticing and questioning to experience each concept from within." },
  { icon: Compass, title: "Rooted in state standards", text: "Georgia Edition follows the Georgia Standards of Excellence; Florida Edition follows B.E.S.T. and NGSSS; Alabama Edition follows the Alabama Course of Study, for every mandatory subject K-12, so academic rigour and self-knowledge grow together." },
];

const Approach = () => (
  <section className="mt-24 grid md:grid-cols-3 gap-5" data-testid="approach-section">
    {PILLARS.map(({ icon: Icon, title, text }, i) => (
      <div key={title} className="rise rounded-3xl p-7 bg-white/60 border border-[#E6DEC3]" style={{ animationDelay: `${i * 0.08}s` }}>
        <Icon className="w-6 h-6 text-[#C87D53]" />
        <h3 className="font-display text-xl text-[#2C4A3E] mt-5">{title}</h3>
        <p className="text-sm text-stone-600 mt-2 leading-relaxed">{text}</p>
      </div>
    ))}
  </section>
);

const Hero = () => {
  const { profile } = useProfile();
  return (
    <section className="grid lg:grid-cols-12 gap-10 items-center pt-10 lg:pt-16">
      <div className="lg:col-span-7 rise">
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Consciousness-based learning · {STATE_NAMES[profile?.state || edition.state]} {profile?.learner_type === "adult" ? "Adult Learner" : "K-12"}</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] leading-[1.05] mt-5">
          Learn the world.<br /><em className="text-[#C87D53] font-normal">Discover who you really are.</em>
        </h1>
        <p className="mt-6 text-base md:text-lg text-stone-600 max-w-xl leading-relaxed">
          Through every subject, students explore the knower as well as the known. Each lesson begins with a centering breath, invites inner active learning, and ends with a reflection on who they are becoming.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {profile?.learner_type === "adult" ? (
            <Link to="/pathway" data-testid="hero-pathway-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white font-medium hover:bg-[#1E352B] transition-colors">Go to my Career Pathway</Link>
          ) : profile ? (
            <a href="#subjects" data-testid="hero-start-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white font-medium hover:bg-[#1E352B] transition-colors">
              Begin learning · {gradeLabel(profile.grade)}
            </a>
          ) : (
            <Link to="/register" data-testid="hero-signup-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white font-medium hover:bg-[#1E352B] transition-colors">Start learning free</Link>
          )}
          <SchoolsCTA />
        </div>
      </div>
      <div className="lg:col-span-5 relative rise" style={{ animationDelay: "0.15s" }}>
        <img src={HERO_IMG} alt="Student learning calmly" className="rounded-[2rem] w-full h-[420px] object-cover shadow-xl" />
        <div className="absolute -bottom-6 -left-6 bg-[#FDFBF7]/90 backdrop-blur-xl rounded-2xl p-4 border border-[#E6DEC3] max-w-[240px] shadow-lg">
          <p className="text-xs uppercase tracking-widest text-stone-500">Today's intention</p>
          <p className="font-display text-[#2C4A3E] mt-1">"I am curious, patient and kind with myself."</p>
        </div>
      </div>
    </section>
  );
};

const QuickStarts = () => {
  const { profile } = useProfile();
  const nav = useNavigate();
  const [quick, setQuick] = useState([]);
  useEffect(() => {
    Promise.all(QUICK_SUBJECTS.map((id) => http.get(`/subjects/${id}`, { params: { grade: profile.grade } })))
      .then((rs) => setQuick(rs.map((r) => ({ subject: r.data.id, label: r.data.short, topic: r.data.topics[0] }))))
      .catch(() => {});
  }, [profile.grade, profile.state]);
  const go = (q) => nav(`/learn/${q.subject}?topic=${encodeURIComponent(q.topic)}`);
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <span className="text-sm text-stone-500 inline-flex items-center gap-1 mr-2"><Sparkles className="w-4 h-4 text-[#D99B26]" />Suggested for {gradeLabel(profile.grade)}:</span>
      {quick.map((q, i) => (
        <button key={q.label} onClick={() => go(q)} data-testid={`quickstart-${i}`}
          className="px-4 py-2 rounded-full bg-white/70 border border-[#E6DEC3] text-sm hover:border-[#2C4A3E] transition-colors">
          {q.label}: <b className="text-[#2C4A3E]">{q.topic}</b>
        </button>
      ))}
    </div>
  );
};

export default function Home() {
  const { profile } = useProfile();
  const [subjects, setSubjects] = useState([]);
  useEffect(() => { http.get("/subjects").then((r) => setSubjects(r.data)); }, [profile?.state]);
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="home-page">
      {!profile && <Hero />}
      {profile ? (
        <>
          <StudentDashboard top={<CheckInPrompt />} cards={[
            { id: "stats", label: "My stats", node: <div className="mt-4"><StatsStrip /></div> },
            { id: "goal", label: "Today's goal", node: <TodayGoal /> },
            { id: "mygoal", label: "My goal", node: <MyGoal /> },
            { id: "words", label: "Word bank", node: <WordsLink /> },
            { id: "badges", label: "Badges", node: <BadgeShelf compact /> },
            ...(pathwayEligible(profile) ? [{ id: "pathway", label: "Career Pathway", node: (
              <Link to="/pathway" data-testid="home-pathway-banner" className="mt-8 flex flex-wrap items-center gap-4 rounded-3xl p-6 bg-[#2C4A3E] text-white hover:bg-[#1E352B] transition-colors">
                <Briefcase className="w-6 h-6 text-[#F2D49B]" />
                <span className="flex-1"><b className="font-display text-xl block">Career Pathway</b><span className="text-sm text-white/80">GED Prep for all four tests + a Business Launch Track to start your own business.</span></span>
                <ArrowUpRight className="w-5 h-5" />
              </Link>) }] : []),
            { id: "quick", label: "Quick starts", node: <div className="mt-8"><QuickStarts /></div> },
            { id: "assigned", label: "Assigned lessons", node: <AssignedSection /> },
          ]} />
        </>
      ) : <><Approach /><PricingSection /></>}
      <section id="subjects" className="mt-16 scroll-mt-24">
        <h2 className="font-display text-3xl text-[#2C4A3E]">{STATE_NAMES[profile?.state || edition.state]}'s mandatory subjects</h2>
        <p className="text-stone-500 mt-2">Each subject is a path to knowing yourself. Choose one to see topics for your grade.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {subjects.map((s, i) => (
            <Link key={s.id} to={profile ? `/subject/${s.id}` : "/register"} data-testid={`subject-card-${s.id}`}
              className={`group rise rounded-3xl p-6 bg-[#F7F3EB] border border-[#E6DEC3] hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300 ${i === 0 ? "lg:col-span-2 lg:row-span-2" : i === subjects.length - 1 ? "lg:col-span-2" : ""}`}
              style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="flex justify-between">
                <span className="w-12 h-12 rounded-2xl grid place-items-center" style={{ background: s.color }}>
                  <SubjectIcon name={s.icon} className="w-5 h-5 text-white" />
                </span>
                <ArrowUpRight className="w-5 h-5 text-stone-400 group-hover:text-[#C87D53] transition-colors" />
              </div>
              <h3 className={`font-display text-[#2C4A3E] mt-6 ${i === 0 ? "text-3xl" : "text-xl"}`}>{s.name}</h3>
              <p className="text-sm text-stone-500 mt-2">{s.standard}</p>
              <p className={`font-display italic text-[#C87D53] mt-4 ${i === 0 ? "text-xl" : "text-sm"}`} data-testid={`subject-self-question-${s.id}`}>"{s.self_question}"</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
